// src/pages/PreviaINSS/ModalPreviaPDF.js
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Box,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
  useContext,
  useEffect,
} from "react";
import { ContainerRenderToPDF } from "../../../components/ContainerRenderPDF";
import html2pdf from "html2pdf.js";
import {
  enviarPreviaInss,
  enviarProjetoInss,
  gravarPdfPrevia,
  confirmarProjetoInss,
  buscarStatusProjetoInss,
  iniciarProjetoPreviaInss,
  liberarPreviaProjetoInss,
} from "../../../services/api-inss.service";
import { PreviaInssContext } from "../PreviaInssContext";
import SnackBar from "../../../components/SnackBar";
import { useProgressBar } from "../../../hooks/useProgressBar";
import { useNavigate } from "react-router-dom";
import {
  usePermissoes,
  CHAVES_INSS_APPROVE,
  CHAVES_INSS_EDIT,
  CHAVES_INSS_EXPORT,
} from "../../../config/Context/PermissoesContext";

const convertBlobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      const base64data = result.split(",")[1];
      resolve(base64data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const normalizarCategoriaMedidaJudicial = (valor) => {
  const txt = String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

  if (txt.includes("DESNECESS")) return "DESNECESSIDADE";
  if (txt.includes("SUGEST")) return "SUGESTAO";
  return "";
};

/**
 * ModalVisualizarPreviaPDF
 *
 * Props adicionais:
 * - flow: 'previa' | 'projeto' (opcional; default 'previa') -> altera título/textos e payload padrão
 * - onEnviar: async function(payload) opcional -> função customizada para enviar (usa enviarPreviaInss/enviarProjetoInss por padrão)
 * - buildPayload: function(base64, dadosContexto) opcional -> constrói payload para o envio
 * - emailDefault: string opcional
 */
const ModalVisualizarPreviaPDF = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const { pode } = usePermissoes();
  const { dados, taxaSelic } = useContext(PreviaInssContext);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const { openProgress, updateProgress, closeProgress, setSuccess, setError } = useProgressBar();

  const {
    open: externalOpen,
    onClose: externalOnClose,
    dadosPreviaPDF = [],
    flow = "previa", // 'previa' | 'projeto'
    cnpj: cnpjProp, // CNPJ passado pelo pai (data.cnpj) - fallback quando dados.cnpj não está no context
    idPreviaInss: idPreviaInssProp, // ID da prévia INSS passado pelo pai
    onEnviar, // optional custom send function
    buildPayload, // optional payload builder
    emailDefault = "gustavo.tolentino@taxationmind.com.br",
    onFecharVersaoAoConfirmar, // ao confirmar projeto: fecha a tela de análise (versão) e volta para listagem
  } = props;

  const cnpjDisponivel =
    dados?.cnpj ?? cnpjProp ?? sessionStorage.getItem("cnpjSelecionado") ?? "";

  const [internalOpen, setInternalOpen] = useState(false);
  const pdfRef = useRef();
  const { setLoading } = useContext(PreviaInssContext);

  // Fluxo de aprovação (apenas para flow "previa"): em_edicao | projeto_iniciado | projeto_confirmado
  const [estadoPrevia, setEstadoPrevia] = useState("em_edicao");
  const [confirmarLiberarOpen, setConfirmarLiberarOpen] = useState(false);
  const [confirmarProjetoOpen, setConfirmarProjetoOpen] = useState(false);
  const [confirmandoProjeto, setConfirmandoProjeto] = useState(false);

  const isControlled = typeof externalOpen !== "undefined";
  const open = isControlled ? externalOpen : internalOpen;
  const onClose = externalOnClose || (() => setInternalOpen(false));

  useImperativeHandle(ref, () => ({
    abrir: () => setInternalOpen(true),
    fechar: () => setInternalOpen(false),
  }));

  // Ao abrir o modal (flow previa), buscar estado salvo no banco e definir estado inicial
  useEffect(() => {
    if (!open || flow !== "previa") return;
    const idPreviaInss = idPreviaInssProp || dados?.idPreviaInss;
    if (!idPreviaInss) {
      setEstadoPrevia("em_edicao");
      return;
    }
    buscarStatusProjetoInss(idPreviaInss)
      .then((res) => {
        if (res?.possui_projeto) {
          if (res?.status === "Confirmado") {
            setEstadoPrevia("projeto_confirmado");
          } else if (res?.status === "Iniciado") {
            setEstadoPrevia("projeto_iniciado");
          } else {
            setEstadoPrevia("em_edicao");
          }
        } else {
          setEstadoPrevia("em_edicao");
        }
      })
      .catch(() => {
        setEstadoPrevia("em_edicao");
      });
  }, [open, flow, idPreviaInssProp, dados?.idPreviaInss]);

  const formatValor = (valor) =>
    valor?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  // --- Novidade: calcular e filtrar valores atualizados da tela Relatório 20s ---
  const getBasesAtualizadasFiltradas = () => {
    try {
      const bases = Array.isArray(dados?.baseCalculo) ? dados.baseCalculo : [];

      // ler filtros persistidos (YYYY-MM)
      const relFilters = JSON.parse(localStorage.getItem("relatorioGeralFilters") || "{}") || {};
      const inicio = relFilters?.inicio || null; // e.g. '2020-07'
      const fim = relFilters?.fim || null;

      const parsePeriod = (period) => {
        if (!period) return null;
        const parts = String(period).split("-");
        if (parts.length < 2) return null;
        const y = Number(parts[0]);
        const m = Number(parts[1]);
        return y * 100 + m;
      };

      const inicioNum = parsePeriod(inicio);
      const fimNum = parsePeriod(fim);

      // replicate cálculo usado em Relatorio20s
      const TAXA_20S = 5.8;
      const VALOR_BASE = 30360;

      const computeValorAtualizado = (base) => {
        const valor = Number(base.valor) || 0;
        const diferencaBase = valor - VALOR_BASE > 0 ? valor - VALOR_BASE : 0;
        const calculo20s = diferencaBase > 0 ? (diferencaBase * TAXA_20S) / 100 : 0;
        const taxaForRef = base.taxaSelic != null ? Number(base.taxaSelic) : 0;
        const acrescimo = calculo20s * (taxaForRef / 100) || 0;
        const valorAtualizado = calculo20s + acrescimo;
        return Number(valorAtualizado) || 0;
      };

      const filtered = bases.filter((b) => {
        if (!b || !b.referencia) return false;
        const periodNum = parsePeriod(b.referencia);
        if (!periodNum) return false;
        if (inicioNum && fimNum) return periodNum >= inicioNum && periodNum <= fimNum;
        if (inicioNum) return periodNum >= inicioNum;
        if (fimNum) return periodNum <= fimNum;
        return true;
      });

      return filtered.map((b) => ({ referencia: b.referencia, valorAtualizado: computeValorAtualizado(b) }));
    } catch (err) {
      console.error('Erro ao calcular bases atualizadas filtradas', err);
      return [];
    }
  };

  // calcular bases atualizadas filtradas e total correspondente
  const basesAtualizadas = getBasesAtualizadasFiltradas();
  const totalAtualizado = basesAtualizadas.reduce((s, b) => s + (b.valorAtualizado || 0), 0);

  const totalGeralBase = dadosPreviaPDF.reduce((acc, item) => acc + (item.valor || 0), 0);
  const totalGeral = totalGeralBase + totalAtualizado;

  const totalDesnecessidade = dadosPreviaPDF
    .filter(
      (item) =>
        normalizarCategoriaMedidaJudicial(item.medidaJudicialCategoria) ===
        "DESNECESSIDADE"
    )
    .reduce((acc, item) => acc + (item.valor || 0), 0);

  const totalSugestaoBase = dadosPreviaPDF
    .filter(
      (item) =>
        normalizarCategoriaMedidaJudicial(item.medidaJudicialCategoria) ===
        "SUGESTAO"
    )
    .reduce((acc, item) => acc + (item.valor || 0), 0);
  // incluir totalAtualizado na sugestão, conforme solicitado
  const totalSugestao = totalSugestaoBase + totalAtualizado;

  const defaultBuildPayload = (base64, dadosContexto) => {
    // Ler filtros do localStorage (mesmos usados para gerar o PDF)
    // Isso garante que os créditos sejam criados com os mesmos filtros aplicados no PDF
    const relFilters = (() => {
      try {
        return JSON.parse(localStorage.getItem('relatorioGeralFilters') || '{}');
      } catch {
        return {};
      }
    })();

    if (flow === "projeto") {
      return {
        idProjetoVersao: dadosContexto.idVersao,
        pdfBase64: base64,
        email: emailDefault,
        inicio: relFilters?.inicio || undefined,
        fim: relFilters?.fim || undefined,
        tipoItem: relFilters?.tipoItem || undefined,
      };
    }
    return {
      idPreviaVersao: dadosContexto.idVersao,
      pdfBase64: base64,
      email: emailDefault,
      inicio: relFilters?.inicio || undefined,
      fim: relFilters?.fim || undefined,
      tipoItem: relFilters?.tipoItem || undefined,
    };
  };

  const defaultEnviar = async (payload) => {
    if (flow === "projeto") return enviarProjetoInss(payload);
    return enviarPreviaInss(payload);
  };

  const handleBaixarPDF = async () => {
    if (!pdfRef.current?.getElement) {
      setObjSnackBar({ type: "error", msg: "Elemento DOM do PDF não encontrado." });
      return;
    }

    try {
      const element = pdfRef.current.getElement();
      const cnpj = cnpjDisponivel || "sem_cnpj";
      const cnpjFormatado = String(cnpj).replace(/\D/g, "");
      const dataStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const filename =
        flow === "projeto"
          ? `projeto_consolidado_${cnpjFormatado}.pdf`
          : `Previa_INSS_${cnpjFormatado}_${dataStr}.pdf`;

      await html2pdf()
        .from(element)
        .set({
          margin: [20, 20, 20, 20],
          jsPDF: { unit: "px", format: [1920, 1080], orientation: "landscape" },
          html2canvas: { scale: 2, useCORS: true },
          filename: filename,
        })
        .save();

      setObjSnackBar({ type: "success", msg: "PDF baixado com sucesso!" });
    } catch (error) {
      console.error("Erro ao baixar PDF:", error);
      setObjSnackBar({ type: "error", msg: "Erro ao baixar PDF." });
    }
  };

  const handleEnviarPrevia = async () => {
    if (!pdfRef.current?.getElement) {
      setObjSnackBar({ type: "error", msg: "Elemento DOM do PDF não encontrado." });
      return;
    }

    try {
      // Captura o elemento DOM do PDF antes de fechar o modal
      const element = pdfRef.current.getElement();

      // Redireciona para listagem de prévias INSS imediatamente
      onClose?.();
      navigate("/esocial/previas");

      openProgress(1, "Preparando envio da prévia e créditos na conta corrente...");
      updateProgress(0, "Preparando dados para envio...");

      // Processa pesado em background, permitindo a navegação/feedback imediato
      setTimeout(async () => {
        try {
          // Gerar PDF em background
          const blob = await html2pdf()
            .from(element)
            .set({
              margin: [20, 20, 20, 20],
              jsPDF: { unit: "px", format: [1920, 1080], orientation: "landscape" },
              html2canvas: { scale: 2, useCORS: true },
            })
            .outputPdf("blob");

          const base64 = await convertBlobToBase64(blob);

          const payloadBuilder = buildPayload || defaultBuildPayload;
          const payloadToSend = payloadBuilder(base64, dados);

          const sendFn = onEnviar || defaultEnviar;

          // logs de debug — confirma flow, payload e função de envio
          console.log("[ModalPreviaPDF] flow:", flow);
          console.log("[ModalPreviaPDF] payloadToSend:", payloadToSend);
          console.log(
            "[ModalPreviaPDF] sendFn:",
            sendFn === enviarProjetoInss ? "enviarProjetoInss" : sendFn === enviarPreviaInss ? "enviarPreviaInss" : "custom"
          );

          // Processar envio em background
          sendFn(payloadToSend)
        .then(async (response) => {
          // Atualizar progresso baseado na resposta
          const creditosCriados = response?.creditosCriados || [];
          const erros = response?.erros || [];
          const totalCreditos = creditosCriados.length + erros.length || 1;

          // Ajustar total na barra para refletir quantidade real
          openProgress(totalCreditos, 'Criando créditos mensais na conta corrente...');
          
          // Iterar créditos criados
          for (let i = 0; i < creditosCriados.length; i++) {
            const idx = i + 1;
            const periodo = creditosCriados[i]?.periodo;
            const mensagem = periodo 
              ? `Crédito criado para o período ${periodo} (${idx}/${totalCreditos})`
              : `Crédito ${idx} de ${totalCreditos} criado com sucesso...`;
            updateProgress(idx, mensagem);
            await new Promise(resolve => setTimeout(resolve, 120));
          }

          // Iterar erros (se houver)
          for (let i = 0; i < erros.length; i++) {
            const idx = creditosCriados.length + i + 1;
            const periodo = erros[i]?.periodo;
            const mensagem = periodo
              ? `Falha ao criar crédito do período ${periodo} (${idx}/${totalCreditos})`
              : `Erro ao criar crédito ${idx} de ${totalCreditos}`;
            updateProgress(idx, mensagem);
            await new Promise(resolve => setTimeout(resolve, 120));
          }

          // Gravar PDF em background (não bloqueia)
          try {
            const idPreviaPayload = dados?.idVersao;
            if (idPreviaPayload) {
              await gravarPdfPrevia({ idPrevia: idPreviaPayload, pdfBase64: base64 });
            }
          } catch (err) {
            console.error('Erro ao gravar PDF no backend:', err);
          }

          const mensagemSucesso = erros.length > 0
            ? `${creditosCriados.length} crédito(s) criado(s) e ${erros.length} com erro. Consulte o suporte se necessário.`
            : `${totalCreditos} crédito(s) criado(s) com sucesso!`;
          
          setSuccess(mensagemSucesso);
          
          // Dispara evento para recarregar a lista de prévias INSS na tela de listagem
          window.dispatchEvent(new CustomEvent('reload-previas-inss'));
        })
        .catch((error) => {
          console.error("Erro ao enviar (modal):", error);
          const errorMsg = error?.response?.data?.mensagem 
            || error?.response?.data?.message 
            || error?.message 
            || "Erro ao processar créditos.";
          
          setError(errorMsg);
        });
        } catch (error) {
          console.error("Erro ao gerar PDF ou enviar créditos:", error);
          setObjSnackBar({ type: "error", msg: "Erro ao gerar PDF ou enviar créditos." });
        }
      }, 0);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      setObjSnackBar({ type: "error", msg: "Erro ao gerar PDF." });
    }
  };

  const titulo = flow === "projeto" ? "Projeto Consolidado" : "Prévia Consolidada";

  const badgeConfig = {
    em_edicao: { texto: "Em Edição", bg: "#e3f2fd", cor: "#1976d2" },
    projeto_iniciado: { texto: "Projeto Iniciado", bg: "#e8f5e9", cor: "#388e3c" },
    projeto_confirmado: { texto: "Projeto Confirmado", bg: "#e8f5e9", cor: "#388e3c" },
  };

  const handleIniciarProjeto = async () => {
    try {
      const cnpjLimpo = String(cnpjDisponivel || "").replace(/\D/g, "");
      const idPreviaInss = idPreviaInssProp || dados?.idPreviaInss;
      if (!cnpjLimpo) {
        setObjSnackBar({ type: "error", msg: "CNPJ não informado." });
        return;
      }
      if (!idPreviaInss) {
        setObjSnackBar({ type: "error", msg: "ID da prévia não informado." });
        return;
      }
      const payload = {
        cnpj: cnpjLimpo,
        id_previa_inss: idPreviaInss,
        valor_total: totalGeral,
        valor_administrativo: totalDesnecessidade,
        valor_judicial: totalSugestao,
        categorias: dadosPreviaPDF,
      };
      await iniciarProjetoPreviaInss(payload);
      setEstadoPrevia("projeto_iniciado");
      setObjSnackBar({ type: "success", msg: "Projeto iniciado com sucesso!" });
    } catch (error) {
      const msg =
        error?.response?.data?.mensagem ||
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao iniciar projeto.";
      setObjSnackBar({ type: "error", msg });
    }
  };

  const handleLiberarPrevia = () => {
    setConfirmarLiberarOpen(true);
  };

  const handleConfirmarLiberar = async () => {
    setConfirmarLiberarOpen(false);
    try {
      const idPreviaInss = idPreviaInssProp || dados?.idPreviaInss;
      if (!idPreviaInss) {
        setObjSnackBar({ type: "error", msg: "ID da prévia não informado." });
        return;
      }
      await liberarPreviaProjetoInss(idPreviaInss);
      setEstadoPrevia("em_edicao");
      setObjSnackBar({ type: "success", msg: "Prévia liberada para edição com sucesso!" });
    } catch (error) {
      const msg =
        error?.response?.data?.mensagem ||
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao liberar prévia.";
      setObjSnackBar({ type: "error", msg });
    }
  };

  const handleConfirmarProjetoClick = () => {
    setConfirmarProjetoOpen(true);
  };

  const handleConfirmarProjeto = async () => {
    setConfirmandoProjeto(true);
    try {
      if (!pdfRef.current?.getElement) {
        setObjSnackBar({ type: "error", msg: "Elemento DOM do PDF não encontrado." });
        setConfirmandoProjeto(false);
        return;
      }

      const cnpjLimpo = String(cnpjDisponivel || "").replace(/\D/g, "");
      if (!cnpjLimpo) {
        setObjSnackBar({ type: "error", msg: "CNPJ não informado." });
        setConfirmandoProjeto(false);
        return;
      }

      // Captura o elemento DOM do PDF antes de fechar o modal
      const element = pdfRef.current.getElement();

      // Fecha o diálogo de confirmação e o modal do PDF
      setConfirmarProjetoOpen(false);
      onClose?.();

      // FECHA A VERSÃO/TELA DE ANÁLISE e vai para listagem (obrigatório ao confirmar projeto)
      if (onFecharVersaoAoConfirmar) {
        onFecharVersaoAoConfirmar();
      } else {
        navigate("/esocial/previas");
      }

      // FEEDBACK: barra de progresso enquanto processa em background
      openProgress(1, "Preparando confirmação do projeto e criação de créditos na conta corrente...");
      updateProgress(0, "Preparando dados para confirmação...");

      // Processa pesado em background, permitindo a navegação/feedback imediato
      setTimeout(async () => {
        try {
          // Gerar PDF em background
          const blob = await html2pdf()
            .from(element)
            .set({
              margin: [20, 20, 20, 20],
              jsPDF: { unit: "px", format: [1920, 1080], orientation: "landscape" },
              html2canvas: { scale: 2, useCORS: true },
            })
            .outputPdf("blob");

          const base64 = await convertBlobToBase64(blob);

          // 1. Confirmar projeto no banco
          const idPreviaInss = idPreviaInssProp || dados?.idPreviaInss;
          if (!idPreviaInss) {
            throw new Error("ID da prévia não informado.");
          }
          const payloadConfirmar = {
            cnpj: cnpjLimpo,
            id_previa_inss: idPreviaInss,
            valor_total: totalGeral,
            valor_administrativo: totalDesnecessidade,
            valor_judicial: totalSugestao,
            categorias: dadosPreviaPDF,
          };
          
          await confirmarProjetoInss(payloadConfirmar);
          setEstadoPrevia("projeto_confirmado");

          // 2. Enviar para conta corrente (criar créditos) - mesmo fluxo do handleEnviarPrevia
          const payloadBuilder = buildPayload || defaultBuildPayload;
          const payloadToSend = payloadBuilder(base64, dados);

          const sendFn = onEnviar || defaultEnviar;

          // Processar envio em background
          sendFn(payloadToSend)
            .then(async (response) => {
              // Atualizar progresso baseado na resposta
              const creditosCriados = response?.creditosCriados || [];
              const erros = response?.erros || [];
              const totalCreditos = creditosCriados.length + erros.length || 1;

              // Ajustar total na barra para refletir quantidade real
              openProgress(totalCreditos, 'Criando créditos mensais na conta corrente...');
              
              // Iterar créditos criados
              for (let i = 0; i < creditosCriados.length; i++) {
                const idx = i + 1;
                const periodo = creditosCriados[i]?.periodo;
                const mensagem = periodo 
                  ? `Crédito criado para o período ${periodo} (${idx}/${totalCreditos})`
                  : `Crédito ${idx} de ${totalCreditos} criado com sucesso...`;
                updateProgress(idx, mensagem);
                await new Promise(resolve => setTimeout(resolve, 120));
              }

              // Iterar erros (se houver)
              for (let i = 0; i < erros.length; i++) {
                const idx = creditosCriados.length + i + 1;
                const periodo = erros[i]?.periodo;
                const mensagem = periodo
                  ? `Falha ao criar crédito do período ${periodo} (${idx}/${totalCreditos})`
                  : `Erro ao criar crédito ${idx} de ${totalCreditos}`;
                updateProgress(idx, mensagem);
                await new Promise(resolve => setTimeout(resolve, 120));
              }

              // Gravar PDF em background (não bloqueia)
              try {
                const idPreviaPayload = dados?.idVersao;
                if (idPreviaPayload) {
                  await gravarPdfPrevia({ idPrevia: idPreviaPayload, pdfBase64: base64 });
                }
              } catch (err) {
                console.error('Erro ao gravar PDF no backend:', err);
              }

              const mensagemSucesso = erros.length > 0
                ? `Projeto confirmado! ${creditosCriados.length} crédito(s) criado(s) e ${erros.length} com erro. Consulte o suporte se necessário.`
                : `Projeto confirmado! ${totalCreditos} crédito(s) criado(s) com sucesso!`;
              
              setSuccess(mensagemSucesso);
              
              // Dispara evento para recarregar a lista de prévias INSS na tela de listagem
              window.dispatchEvent(new CustomEvent('reload-previas-inss'));
            })
            .catch((error) => {
              console.error("Erro ao enviar créditos para conta corrente:", error);
              const errorMsg = error?.response?.data?.mensagem 
                || error?.response?.data?.message 
                || error?.message 
                || "Erro ao processar créditos.";
              
              setError(`Projeto confirmado, mas houve erro ao criar créditos: ${errorMsg}`);
            });
        } catch (error) {
          console.error("Erro ao confirmar projeto:", error);
          const msg =
            error?.response?.data?.mensagem ||
            error?.response?.data?.message ||
            error?.message ||
            "Erro ao confirmar projeto.";
          setError(msg);
        } finally {
          setConfirmandoProjeto(false);
        }
      }, 0);
    } catch (error) {
      const msg =
        error?.response?.data?.mensagem ||
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao confirmar projeto.";
      setObjSnackBar({ type: "error", msg });
      setConfirmandoProjeto(false);
    }
  };

  const showFluxoAprovacao = flow === "previa";
  const badge = showFluxoAprovacao ? badgeConfig[estadoPrevia] || badgeConfig.em_edicao : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "nowrap" }}>
        <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>{titulo}</span>
        {badge && (
          <Box
            component="span"
            sx={{
              bgcolor: badge.bg,
              color: badge.cor,
              borderRadius: "12px",
              fontSize: 12,
              fontWeight: 500,
              px: 1.5,
              py: 0.5,
              transition: "all 0.3s ease",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {badge.texto}
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers style={{ backgroundColor: "#fff" }}>
        <ContainerRenderToPDF
          ref={pdfRef}
          filename={flow === "projeto" ? "relatorio-projeto.pdf" : "relatorio-previa.pdf"}
          orientation="portrait"
          sx={{ padding: "20px", backgroundColor: "#fff" }}
        >
          <Typography variant="h6" gutterBottom>
            Resumo das Categorias
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#002d72" }}>
                <TableCell sx={cellStyleHead}>Item</TableCell>
                <TableCell sx={cellStyleHead}>Verba Trabalhista (Rubricas)</TableCell>
                <TableCell sx={cellStyleHead}>Fundamentação</TableCell>
                <TableCell sx={cellStyleHead} align="right">Valor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dadosPreviaPDF.map((item, idx) => {
                const categoria = normalizarCategoriaMedidaJudicial(
                  item.medidaJudicialCategoria
                );
                const corLinha =
                  categoria === "SUGESTAO" ? "#dceefb"
                  : categoria === "DESNECESSIDADE" ? "#e3e3e3"
                  : "#ffffff";

                return (
                  <TableRow key={idx} sx={{ backgroundColor: corLinha }}>
                    <TableCell sx={cellStyle} />
                    <TableCell sx={cellStyle}>{item.descricaoCategoria}</TableCell>
                    <TableCell sx={cellStyle}>{item.fundamentacaoCategoria}</TableCell>
                    <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{formatValor(item.valor)}</TableCell>
                  </TableRow>
                );
              })}

              {/* Inserir aqui a linha única com o total dos Valores Atualizados (20s) — aparece após os itens dinâmicos e antes do Total geral */}
              {basesAtualizadas && basesAtualizadas.length > 0 && (
                <TableRow sx={{ backgroundColor: "#dceefb" }}>
                  <TableCell sx={cellStyle} />
                  <TableCell sx={cellStyle}>Limitação 20 Salários</TableCell>
                  <TableCell sx={cellStyle}>Artigo 4º da Lei nº 6.950/1981 (cálculo funcionário x funcionário - salário de contribuição)</TableCell>
                  <TableCell sx={{ ...cellStyle, textAlign: "right" }}>{formatValor(totalAtualizado)}</TableCell>
                </TableRow>
              )}

               <TableRow>
                 <TableCell sx={noBorder} />
                 <TableCell sx={noBorder} />
                 <TableCell sx={totalStyle}>Total</TableCell>
                 <TableCell align="right" sx={totalStyle}>{formatValor(totalGeral)}</TableCell>
               </TableRow>

               <TableRow>
                 <TableCell sx={noBorder} />
                 <TableCell sx={noBorder} />
                 <TableCell sx={{ ...totalStyle, backgroundColor: "#e3e3e3" }}>Desnecessidade de medida judicial</TableCell>
                 <TableCell align="right" sx={{ ...totalStyle, backgroundColor: "#e3e3e3" }}>{formatValor(totalDesnecessidade)}</TableCell>
               </TableRow>

               <TableRow>
                 <TableCell sx={noBorder} />
                 <TableCell sx={noBorder} />
                 <TableCell sx={{ ...totalStyle, backgroundColor: "#dceefb" }}>Sugestão de medida judicial</TableCell>
                 <TableCell align="right" sx={{ ...totalStyle, backgroundColor: "#dceefb" }}>{formatValor(totalSugestao)}</TableCell>
               </TableRow>
            </TableBody>
          </Table>

          <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
        </ContainerRenderToPDF>
      </DialogContent>

      <DialogActions sx={{ flexWrap: "wrap", gap: 1, justifyContent: "space-between", px: 2 }}>
        <Button
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ color: "#6c757d", "&:hover": { backgroundColor: "rgba(108,117,125,0.08)", color: "#5a6268" } }}
        >
          FECHAR
        </Button>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {showFluxoAprovacao ? (
            <>
              {estadoPrevia === "em_edicao" && pode(CHAVES_INSS_EDIT) && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleIniciarProjeto}
                  sx={{
                    backgroundColor: "#007bff",
                    "&:hover": { backgroundColor: "#0056b3" },
                  }}
                >
                  Iniciar Projeto
                </Button>
              )}
              {estadoPrevia === "projeto_iniciado" && (
                <>
                  {pode(CHAVES_INSS_APPROVE) && (
                    <Button
                      variant="contained"
                      startIcon={<ArrowBackIcon />}
                      onClick={handleLiberarPrevia}
                      sx={{
                        backgroundColor: "#ffc107",
                        color: "#000",
                        "&:hover": { backgroundColor: "#e0a800", color: "#000" },
                      }}
                    >
                      Liberar Prévia
                    </Button>
                  )}
                  {pode(CHAVES_INSS_EDIT) && (
                    <Button
                      variant="contained"
                      startIcon={<CheckIcon />}
                      onClick={handleConfirmarProjetoClick}
                      disabled={confirmandoProjeto}
                      sx={{
                        backgroundColor: "#28a745",
                        "&:hover": { backgroundColor: "#218838" },
                      }}
                    >
                      {confirmandoProjeto ? "Confirmando..." : "Confirmar Projeto"}
                    </Button>
                  )}
                </>
              )}
            </>
          ) : (
            dados?.isEditable !== false &&
            pode(CHAVES_INSS_EDIT) && (
              <Button variant="contained" onClick={handleEnviarPrevia}>
                ENVIAR
              </Button>
            )
          )}
          {pode(CHAVES_INSS_EXPORT) && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleBaixarPDF}
              sx={{
                color: "#007bff",
                borderColor: "#007bff",
                "&:hover": { borderColor: "#0056b3", backgroundColor: "rgba(0,123,255,0.04)" },
              }}
            >
              BAIXAR PDF
            </Button>
          )}
        </Box>
      </DialogActions>

      {/* Modal confirmação Liberar Prévia */}
      <Dialog open={confirmarLiberarOpen} onClose={() => setConfirmarLiberarOpen(false)}>
        <DialogContent>
          <Typography>Deseja realmente liberar a prévia para edição?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarLiberarOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmarLiberar} variant="contained" color="warning">
            Sim, Liberar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal confirmação Confirmar Projeto */}
      <Dialog open={confirmarProjetoOpen} onClose={() => !confirmandoProjeto && setConfirmarProjetoOpen(false)}>
        <DialogContent>
          <Typography>Deseja confirmar o projeto? Esta ação não poderá ser desfeita.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarProjetoOpen(false)} disabled={confirmandoProjeto}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarProjeto}
            variant="contained"
            color="success"
            disabled={confirmandoProjeto}
          >
            {confirmandoProjeto ? "Confirmando..." : "Sim, Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
});

const cellStyleHead = { border: "1px solid #0b0b0b", color: "#fff", fontWeight: "bold" };
const cellStyle = { border: "1px solid #0b0b0b" };
const noBorder = { border: "none" };
const totalStyle = { border: "1px solid #0b0b0b", fontWeight: "bold" };

export default ModalVisualizarPreviaPDF;
